import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import {
  AdminFilterSelect,
  AdminPageHeader,
  AdminStatusBadge,
  AdminTableCard,
} from './AdminUi';
import './admin.css';

const TYPE_OPTIONS = [
  { value: 'POST', label: 'POST (image)' },
  { value: 'VIDEO', label: 'VIDEO' },
];

export default function AdminHealthContent() {
  const [data, setData] = useState({ total: 0, results: [] });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', description: '', content_type: 'POST' });
  const [file, setFile] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('admin/health-contents/', { params: { limit: 100 } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.error || 'Failed'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('description', form.description);
    fd.append('content_type', form.content_type);
    if (file) {
      if (form.content_type === 'VIDEO') fd.append('video', file);
      else fd.append('image', file);
    }
    try {
      await api.post('admin/health-contents/create/', fd);
      toast.success('Created');
      setForm({ title: '', description: '', content_type: 'POST' });
      setFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Create failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this waiting-room content?')) return;
    await api.delete(`admin/health-contents/${id}/`);
    toast.success('Deleted');
    load();
  };

  return (
    <div>
      <AdminPageHeader
        title="Waiting Room Content"
        subtitle={`Health tips shown in waiting room (${data.total})`}
      />

      <div className="admin-card mb-4 overflow-visible">
        <div className="p-3 p-md-4">
          <h6 className="fw-bold mb-3">Add content</h6>
          <form onSubmit={create} className="row g-2">
            <div className="col-12 col-md-4">
              <input
                className="form-control"
                placeholder="Title"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div className="col-12 col-md-3">
              <AdminFilterSelect
                className="admin-filter-dd--fluid"
                value={form.content_type}
                onChange={(content_type) => setForm({ ...form, content_type })}
                options={TYPE_OPTIONS}
                placeholder="Content type"
                aria-label="Content type"
              />
            </div>
            <div className="col-12 col-md-5">
              <input
                type="file"
                className="form-control"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
            <div className="col-12">
              <textarea
                className="form-control"
                rows={2}
                placeholder="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="col-12">
              <button className="btn btn-primary rounded-pill px-4" type="submit">
                Create
              </button>
            </div>
          </form>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      ) : (
        <>
          <div className="admin-mobile-list d-md-none">
            {data.results.length === 0 ? (
              <div className="admin-card p-4 text-center text-muted">No content yet</div>
            ) : (
              data.results.map((h) => (
                <article key={h.id} className="admin-mobile-card">
                  <div className="admin-mobile-card-top">
                    <div className="min-w-0">
                      <div className="admin-mobile-card-id">#{h.id}</div>
                      <div className="admin-mobile-card-title">{h.title}</div>
                      <div className="admin-mobile-card-sub">{h.description || '—'}</div>
                    </div>
                    <AdminStatusBadge status={h.content_type} />
                  </div>
                  <div className="admin-mobile-card-meta">
                    <div>
                      <span className="label">Media</span>
                      <span className="value">
                        {h.image && (
                          <a href={h.image} target="_blank" rel="noreferrer">Image</a>
                        )}
                        {h.video && (
                          <a href={h.video} target="_blank" rel="noreferrer">Video</a>
                        )}
                        {!h.image && !h.video && '—'}
                      </span>
                    </div>
                  </div>
                  <div className="admin-mobile-card-actions">
                    <button
                      className="btn btn-sm btn-outline-danger rounded-pill px-3 w-100"
                      onClick={() => remove(h.id)}
                    >
                      Delete
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>

          <div className="d-none d-md-block">
            <AdminTableCard>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th><th>Title</th><th>Type</th><th>Media</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((h) => (
                    <tr key={h.id}>
                      <td>{h.id}</td>
                      <td>
                        <div className="fw-semibold">{h.title}</div>
                        <div className="small text-muted">{h.description}</div>
                      </td>
                      <td><AdminStatusBadge status={h.content_type} /></td>
                      <td className="small">
                        {h.image && <a href={h.image} target="_blank" rel="noreferrer">Image</a>}
                        {h.video && <a href={h.video} target="_blank" rel="noreferrer">Video</a>}
                        {!h.image && !h.video && '—'}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-danger rounded-pill px-3"
                          onClick={() => remove(h.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableCard>
          </div>
        </>
      )}
    </div>
  );
}
